import { IconChevronDown } from "@components/ui/Icon"
import { useSiteTheme } from "@features/header/header-theme"
import { useMegaMenu, useMegaMenuButtonProps } from "@hooks/useMegaMenu"
import { tv } from "@utils/tw"
import {
  ContentBlocks,
  defineBlock,
  EditableText,
  SlotBlocks,
  useBlockContext,
  useInlineEditableValue,
  useInnerBlocks,
} from "eddev/blocks"
import { useRouter } from "eddev/routing"
import { Suspense, useEffect, useId, useRef } from "react"
import { createPortal } from "react-dom"

export const meta: BlockMeta = {
  title: "Mega Menu Page",
  icon: "admin-links",
  lazyLoad: false,
}

export default defineBlock("parts/megamenu-page", (props) => {
  const id = "megamenu-" + useBlockContext()?.index
  const ctx = useMegaMenu()

  const [sectionLabel] = useInlineEditableValue<string>("label")

  const innerBlocks = useInnerBlocks()

  const contents = (
    <div
      className="relative"
      data-mixpanel-area="Desktop Megamenu Page"
      data-mixpanel-menu={sectionLabel.replace("&ampl", "&")}
    >
      <div className="grid-auto">
        {["col1", "col2"].map((id) => (
          <div className="col-span-3" key={id}>
            {env.admin ? (
              <SlotBlocks id={id} template={[["parts/megamenu-column", {}]]} templateLock="insert" />
            ) : (
              <Suspense>
                <Focus>
                  <ContentBlocks
                    blocks={innerBlocks.filter((b) => (b as any).slotId === id).flatMap((b) => b.innerBlocks ?? [])}
                  />
                </Focus>
              </Suspense>
            )}
          </div>
        ))}
        <div className="col-span-6 relative -mr-grid-margin">
          {env.admin ? (
            <SlotBlocks id="cta" template={[["parts/megamenu-cta", {}]]} templateLock="insert" />
          ) : (
            <Suspense>
              <Focus>
                <ContentBlocks
                  blocks={innerBlocks.filter((b) => (b as any).slotId === "cta").flatMap((b) => b.innerBlocks ?? [])}
                />
              </Focus>
            </Suspense>
          )}
        </div>
      </div>
    </div>
  )

  const styles = buttonStyles({
    active: ctx.activeItem === id,
  })

  const buttonProps = useMegaMenuButtonProps(id, contents)

  return (
    <>
      <button {...buttonProps} className={styles.base()} data-keyboard-navigable data-keyboard-id={id}>
        <EditableText as="span" className={styles.label()} store="label" placeholder="Label" plainText />
        <IconChevronDown className={styles.chevron()} />
      </button>
      {env.admin &&
        ctx.adminRenderTarget &&
        ctx.activeItem === id &&
        createPortal(contents, ctx.adminRenderTarget as HTMLDivElement)}
    </>
  )
})

function Focus({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const handleLinkClick = useRouter((router) => router.handleClickEvent)

  useEffect(() => {
    const links = ref.current?.querySelectorAll("li > a") as NodeListOf<HTMLAnchorElement>
    links?.forEach((link, i) => {
      link.setAttribute("data-keyboard-navigable", "")
      link.addEventListener("keydown", (e) => {
        if (e.key === " " || e.key === "Enter") {
          handleLinkClick(e as any, link.href)
        }
      })
    })
  })

  return <div ref={ref}>{children}</div>
}

const buttonStyles = tv({
  base: [
    "text-fg type-ui-l bg-transparent",
    "pl-3 pr-1 -ml-3 rounded-md",
    "transition-colors",
    "flex items-center justify-between",
  ],
  slots: {
    chevron: "",
    label: "py-[0.6em]",
  },
  variants: {
    active: {
      true: {
        base: "bg-bg-low",
      },
    },
  },
})
